// React
import React, { useState, useEffect, useCallback } from 'react';
import i18next from 'i18next';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// Component
import { toLowercaseReplaceSpace } from '../../../shared/shared-function/helperFunctions';

// Data
import idConstants, { REGION } from '../../../data/idConstants';
import { GAEventAction } from '../../../data/ga-constants';
import {
  accessRoles,
  regionDetails,
  activeCategoryIdInRegion,
  sidenavRoles
} from '../../../data/appConstants';

// utils
import { getRegionCode } from '../../../utils/get-region-details';
import { CDN_LINK } from '../../../assets/img';
import { handleOrderNavigation, sellerApiRestrictedRoles } from '../../../utils';

// Action
import { getCategories } from '../../../actions/CategoryManagementAction';
import { retrieveUserCompanyStatus } from '../../../actions/CompanyAction';

// Css
import './index.scss';

const SideNavigation = ({
  data,
  linkBuyerAccount,
  open,
  handleMenuClick,
  customClassName
}) => {
  const dispatch = useDispatch();
  const isOpen = true;
  const [localSellerCompanyId, setLocalSellerCompanyId] = useState('');
  const {
    companyStatus,
    categoryList,
    sidebarContent,
    user,
    tenantConfig,
    csmTenants
  } = useSelector((state) => {
    return {
      companyStatus: state.CompanyDetailsReducer.userCompanyStatus,
      categoryList: state.CategoryManagementReducer.categoryList,
      sidebarContent: state.ContentReducer.sidebarContent,
      user: state.loginReducer.user,
      tenantConfig: state?.TenantConfigReducer?.tenantConfig,
      csmTenants: state.UserServiceReducer?.csmTenants
    };
  });
  const role = user?.roles?.[0];

  const [navigationOptionsData, setNavigationOptionsData] = useState([]);
  const isSeller = user?.roles?.includes(accessRoles.seller);
  const isReseller = user?.roles?.includes(accessRoles.reseller);
  const isSalesRep = user?.roles?.includes(accessRoles.sales_rep);
  const isUspAdmin = user?.roles?.includes(accessRoles.usp_admin);
  const salesRepSelectedCompany = localStorage.getItem(
    idConstants.selectedCompanyObject.selectedCompany
  );
  const rolesWithoutSellerApi = sellerApiRestrictedRoles(user?.roles);

  const isRoleEmpty = (role) => !role || role === 'undefined' || role === 'null';
  const updatedNavigationOptionsData = useCallback(
    (options) => {
      // If user has no roles, show only dashboard
      if (isRoleEmpty(role)) {
        return (
          options?.filter(
            (item) => item.key === '101' || item.link === '/dashboard'
          ) || []
        );
      }

      //function to check if user has required role for a menu item
      const hasRequiredRole = (menuItem) => {
        if (!menuItem.roles || menuItem.roles.length === 0) return true;
        return menuItem?.roles?.includes(role);
      };

      // Hide Myproducts till salesrep selects the company and for other roles return true
      const validateSalesRep = (item) => {
        if (isSalesRep && item.key === '102') {
          return salesRepSelectedCompany;
        }
        return true;
      };

      // CSM User Management should only show if csmTenants.tenantId is 'main'
      const validateCsmUserManagement = (item) => {
        if (item.link === '/dashboard/user-onboarding' && role === accessRoles.csm) {
          return csmTenants?.tenantId === 'main';
        }
        return true;
      };

      // Discount Management should only show in IN region
      const validateDiscountManagement = (item) => {
        if (item.link === '/dashboard/discount-management') {
          return process.env.REACT_APP_REGION === REGION.IN.CODE;
        }
        return true;
      };

      // Shipping Management should only show in specific regions
      const validateShippingManagement = (item) => {
        if (item.link === '/dashboard/shipping-management') {
          return process.env.REACT_APP_REGION === REGION.IN.CODE;
        }
        return true;
      };

      // Recursive function to filter menu items at any level
      const filterMenuItems = (items) => {
        return items
          .filter(
            (item) =>
              hasRequiredRole(item) &&
              validateSalesRep(item) &&
              validateCsmUserManagement(item) &&
              validateDiscountManagement(item) &&
              validateShippingManagement(item)
          )
          .map((item) => {
            // Recursively process all submenu types
            const processedItem = { ...item };
            if (item.subMenus) {
              processedItem.subMenus = filterMenuItems(item.subMenus);
            }
            if (item.subMenu) {
              processedItem.subMenu = filterMenuItems(item.subMenu);
            }
            return processedItem;
          });
      };

      return filterMenuItems(options || []);
    },
    [isReseller, isSalesRep, isSeller, salesRepSelectedCompany, role, csmTenants]
  );

  const callRetrieveUserCompanyStatus = () => {
    dispatch(retrieveUserCompanyStatus());
  };

  useEffect(() => {
    if (rolesWithoutSellerApi?.length === 0) {
      callRetrieveUserCompanyStatus();
    }
  }, []);

  useEffect(() => {
    if (sidebarContent?.menu) {
      const updatedNavigationOptions = updatedNavigationOptionsData(
        sidebarContent?.menu
      );
      if (updatedNavigationOptions) {
        setNavigationOptionsData([...updatedNavigationOptions]);
      }
    }
    if (rolesWithoutSellerApi?.length === 0) {
      callRetrieveUserCompanyStatus();
    }
  }, [sidebarContent, user, salesRepSelectedCompany, updatedNavigationOptionsData]);

  useEffect(() => {
    const updateDataFromLocalStorage = () => {
      const localStorageData = localStorage.getItem(
        idConstants.salesRepSelectedCompany.selectedCompany
      );
      if (localStorageData) {
        setLocalSellerCompanyId(localStorageData);
      }
    };
    updateDataFromLocalStorage();
    window.addEventListener(idConstants.sellerCompanyId, updateDataFromLocalStorage);
    return () => {
      window.removeEventListener(
        idConstants.sellerCompanyId,
        updateDataFromLocalStorage
      );
    };
  }, [localStorage.getItem(idConstants.salesRepSelectedCompany.selectedCompany)]);

  const handleSellerSideNavigation = () => {
    if (
      companyStatus.companyRegisteredStatus &&
      companyStatus.companyRegisteredStatus === 'approved' &&
      categoryList?.length === 0
    ) {
      dispatch(getCategories(idConstants.submitted.statusId)).then((response) => {
        if (response) {
          const navigationData = getSideBarMenus(response);
          const updatedNavigationOptions =
            updatedNavigationOptionsData(navigationData);
          if (updatedNavigationOptions) {
            setNavigationOptionsData([...updatedNavigationOptions]);
          }
        }
      });
    } else {
      const navigationData = getSideBarMenus(categoryList);
      if (navigationData) {
        const updatedNavigationOptions =
          updatedNavigationOptionsData(navigationData);
        if (updatedNavigationOptions) {
          setNavigationOptionsData([...updatedNavigationOptions]);
        }
      }
    }
  };

  useEffect(() => {
    if ((role !== accessRoles.seller && localSellerCompanyId) || isUspAdmin) {
      dispatch(getCategories(idConstants.submitted.statusId)).then((response) => {
        if (response) {
          const navigationData = getSideBarMenus(response);
          let myproductsData = navigationData?.filter(
            (item) =>
              item.link === '/dashboard' || item.link === '/dashboard/myproducts'
          );
          setNavigationOptionsData(myproductsData);
        }
      });
    }
    if (
      role !== accessRoles.sales_rep ||
      (role === accessRoles.sales_rep && (isReseller || isSeller))
    ) {
      handleSellerSideNavigation();
    }
  }, [
    companyStatus,
    localSellerCompanyId,
    role,
    sidebarContent,
    isReseller,
    isSeller
  ]);

  const getSideBarMenus = (data) => {
    if (data?.content?.length > 0) {
      const dynamicData = data.content
        .map((category) => ({
          id: category.productCategoryName,
          name: category.productCategoryName,
          categoryId: category.productCategoryId,
          icon: (
            <svg
              className='MuiSvgIcon-root'
              focusable='false'
              viewBox='0 0 24 24'
              aria-hidden='true'
            >
              <path d='M22 11V3h-7v3H9V3H2v8h7V8h2v10h4v3h7v-8h-7v3h-2V8h2v3z'></path>
            </svg>
          ),
          link: `/dashboard/my-products/${toLowercaseReplaceSpace(
            category.productCategoryName
          )}`,
          possibleUrls: `/dashboard/my-products/${category.productCategoryName}`,
          key: category.productCategoryName,
          hasSubMenu: true,
          disabled: false,
          hide: false,
          displayOptions: false,
          //GA-202
          trackEventData: {
            eventCategory: 'side_bar',
            eventAction: GAEventAction.clicked,
            eventLabel: category?.productCategoryName
          }
        }))
        .filter((item) =>
          getRegionCode() !== regionDetails[0].regionCode
            ? activeCategoryIdInRegion[process.env.REACT_APP_REGION]?.includes(
                item.categoryId
              )
            : true
        );

      const navigationData = sidebarContent?.menu?.map((navigation) => {
        // This condition applies only to the IN region and the "My Products" section (2).
        if (
          process.env.REACT_APP_REGION === REGION.IN.CODE &&
          navigation.id === '2'
        ) {
          return {
            ...navigation,
            subMenus: [
              ...navigation.subMenus,
              ...(!tenantConfig?.sidebar?.showDynamicCategories ? [] : dynamicData)
            ]
          };
        }
        return navigation;
      });
      return navigationData;
    }
  };

  const isCompanyApproved =
    companyStatus?.companyRegisteredStatus === 'approved' &&
    companyStatus?.areCompanyDetailsCaptured === true;

  const onSubmenuClicked = (item, level, childItem) => {
    const updateDisplayOptions = (data, id) => {
      if (data.id === id) {
        data.displayOptions = !data.displayOptions;
      } else {
        data.displayOptions = false;
      }
      return data;
    };

    const obj = [...navigationOptionsData];

    if (level === 1) {
      obj.forEach((data) => {
        data.hasSubMenu
          ? updateDisplayOptions(data, item.id)
          : updateDisplayOptions(data, null);
      });
    }

    if (level === 2) {
      obj.forEach((data) => {
        if (data.id === item.id && data.displayOptions) {
          data.subMenus.forEach((subItem) => {
            updateDisplayOptions(subItem, childItem.id);
          });
        } else {
          data.subMenus?.forEach((subItem) => updateDisplayOptions(subItem, null));
        }
      });
    }

    setNavigationOptionsData(obj);
  };

  const setIcon = (icon, name) => {
    if (icon?.props) {
      return {
        ...icon,
        props: {
          ...icon.props,
          name: i18next.t(icon.props.name)
        }
      };
    }

    if (icon?.class) {
      return <i className={icon.class} title={name} />;
    }

    if (icon?.img) {
      return (
        <img
          className='side-navigation-menu-icon'
          src={`${CDN_LINK}/${icon.img}`}
          alt={name}
          title={name}
        />
      );
    }

    return null;
  };

  const setMenuIcon = (item) => {
    const { icon, name } = item || {};
    return setIcon(icon, name);
  };

  const subMenuIcon = (subItem) => {
    const { icon, name } = subItem || {};

    if (icon?.class === 'MuiSvgIcon-root a-icon') {
      return (
        <svg
          className='MuiSvgIcon-root'
          focusable='false'
          viewBox='0 0 24 24'
          aria-hidden='true'
        >
          <path d='M22 11V3h-7v3H9V3H2v8h7V8h2v10h4v3h7v-8h-7v3h-2V8h2v3z'></path>
        </svg>
      );
    }

    return setIcon(icon, name);
  };

  return (
    <div
      className={`${!open ? 'side-navigation-hide' : 'side-navigation'} ${customClassName}`}
    >
      <nav
        className={`m-side-navigation nav_alignment ${isOpen ? '-open' : ''} ${
          sidenavRoles.includes(role) || isCompanyApproved || isRoleEmpty(role)
            ? ''
            : 'side-navigation-disabled'
        }`}
        aria-label='Side Navigation'
        aria-hidden='false'
      >
        <ul className='m-side-navigation__menuItems' role='menubar'>
          {navigationOptionsData?.map((item) => {
            const icon = setMenuIcon(item);

            if (!item?.hasSubMenu) {
              return (
                <li
                  className='m-side-navigation__menuItem'
                  role='none'
                  key={item?.id}
                  onClick={() => handleMenuClick(false, item.trackEventData)}
                >
                  <Link
                    to={item?.link}
                    role='menuitem'
                    className='m-side-navigation__link'
                  >
                    {icon}
                    <span className='m-side-navigation__label'>
                      {i18next.t(item?.name)}
                    </span>
                  </Link>
                </li>
              );
            }
            if (item.hasSubMenu) {
              return (
                <li
                  className={`m-side-navigation__menuItem ${
                    item.displayOptions && '-open'
                  }`}
                  role='none'
                  key={item?.id}
                >
                  <button
                    type='button'
                    className='m-side-navigation__group --nopadding'
                    aria-haspopup='true'
                    role='menuitem'
                  >
                    <Link
                      to={handleOrderNavigation(role, item?.link)}
                      role='menuitem'
                      className='m-side-navigation__link'
                      onClick={() => handleMenuClick(false, item.trackEventData)}
                    >
                      {icon}
                      <span className='m-side-navigation__label'>
                        {i18next.t(item.name)}
                      </span>
                    </Link>
                    <i
                      className='a-icon arrow boschicon-bosch-ic-down'
                      title='down'
                      onClick={() => onSubmenuClicked(item, 1)}
                      tabIndex={0}
                      role='button'
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ')
                          onSubmenuClicked(item, 1);
                      }}
                    />
                  </button>
                  <ul
                    className='m-side-navigation__menuSubitems -sub-menu'
                    role='menu'
                  >
                    {item?.subMenus?.map((subItem) => {
                      const icon = subMenuIcon(subItem);

                      if (!subItem.subMenu?.length) {
                        return (
                          <li
                            className='m-side-navigation__menuItem -sub-menu'
                            role='none'
                            key={subItem.id}
                            onClick={() =>
                              handleMenuClick(false, subItem.trackEventData)
                            }
                          >
                            <Link
                              to={subItem.link}
                              role='menuitem'
                              className='m-side-navigation__link'
                              tabIndex={-1}
                            >
                              <span>{icon}</span>
                              <span className='m-side-navigation__label'>
                                {i18next.t(subItem.name)}
                              </span>
                            </Link>
                          </li>
                        );
                      }
                      if (subItem.subMenu) {
                        return (
                          <li
                            className={`m-side-navigation__menuItem -sub-menu`}
                            key={subItem.id}
                            role='none'
                          >
                            <button
                              type='button'
                              className='m-side-navigation__group sub-menu-btn --nopadding'
                              aria-haspopup='true'
                              role='menuitem'
                            >
                              <Link
                                to={subItem.link}
                                role='menuitem'
                                className='m-side-navigation__link'
                                onClick={() =>
                                  handleMenuClick(false, subItem.trackEventData)
                                }
                              >
                                {icon}
                                <span className='m-side-navigation__label'>
                                  {i18next.t(subItem.name)}
                                </span>
                              </Link>
                              <i
                                className={`a-icon arrow boschicon-bosch-ic-${
                                  subItem.displayOptions ? 'down' : 'up'
                                }`}
                                title='down'
                                onClick={() => onSubmenuClicked(item, 2, subItem)}
                                tabIndex={0}
                                role='button'
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ')
                                    onSubmenuClicked(item, 2, subItem);
                                }}
                              />
                            </button>
                            <ul
                              className='m-side-navigation__menuSubitems'
                              role='menu'
                            >
                              {subItem.subMenu.map((subMenu) => {
                                const translateTitle = (name) => {
                                  return i18next.t(name);
                                };

                                const icon = { ...subMenu?.icon };

                                if (subMenu?.icon?.props) {
                                  icon.props = { ...subMenu?.icon?.props };
                                  icon.props.name = translateTitle(
                                    subMenu?.icon?.props.name
                                  );
                                }
                                return (
                                  subItem.displayOptions && (
                                    <li
                                      className='m-side-navigation__menuItem -sub-menu'
                                      role='none'
                                      key={subMenu.id}
                                      onClick={() =>
                                        handleMenuClick(
                                          false,
                                          subMenu.trackEventData
                                        )
                                      }
                                    >
                                      <Link
                                        to={subMenu.link}
                                        role='menuitem'
                                        className='m-side-navigation__link sub-menu-options'
                                      >
                                        <span className='m-side-navigation__label'>
                                          {i18next.t(subMenu.name)}
                                        </span>
                                      </Link>
                                    </li>
                                  )
                                );
                              })}
                            </ul>
                          </li>
                        );
                      }
                      return null;
                    })}
                  </ul>
                </li>
              );
            }
            return null;
          })}
        </ul>
        {/* Commenting as per MS-16579*/}
        {/* <Link role='menuitem' className='m-side-navigation__link'>
          <div className='switch__market__link'>
            {<i className='a-icon boschicon-bosch-ic-up-down' title='switch' />}
            <span
              className='m-side-navigation__label -size-m'
              onClick={() => linkBuyerAccount()}
            >
              Switch to Marketplace
            </span>
          </div>
        </Link> */}
      </nav>
    </div>
  );
};
export default SideNavigation;
